import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';

import getLocationFromCity from '../functions/getLocationFromCity';
import getCityFromLocation from '../functions/getCityFromLocation';
import getProvincePrincipalCity from '../functions/getProvincePrincipalCity';
import useSessionStore from '../hooks/useSessionStore';
import { PROVINCE } from '../definitions/Provinces';
import LegacyCityPropType from '../definitions/LegacyCityPropType';
import MessagePropType from '../definitions/MessagePropType';
import CityInput from './CityInput';
import { AnimationTypePropType, ANIMATION_TYPES } from './AnimatedContainer';
import InputController from './InputController';


function LocationSelector({
    id,
    name,

    label,
    placeholder,

    onChange,
    onValidityChange,

    value,
    initialProvince,
    
    isRequired,
    isDisabled,

    isVisible,
    animationType = ANIMATION_TYPES.FADE,

    className,
    
    ...otherProps
}) {
    return (
        <InputController
            id={id}
            name={name}
            value={value}
            isRequired={isRequired}
            isDisabled={isDisabled}
            onValidityChange={onValidityChange}
            onChange={onChange}
            isVisible={isVisible}
            animationType={animationType}
            className={className}
            {...otherProps}
        >
            {(inputProps, inputState) => {
                const sessionStore = useSessionStore();

                const isLoading = !sessionStore.hasFetchedSession && !value?.name;
                const { interestLocation } = sessionStore;
                const disabled = inputState.isDisabled || isLoading;
                const currentValue = inputState.parsedValue ?? getDefaultCity({ value, interestLocation, initialProvince });

                // If the interest location changes, we want to update our value if it's not set.
                useEffect(() => {
                    if (value == null) {
                        inputState.parsedValue = getDefaultCity({ value, interestLocation, initialProvince });
                    }
                }, [ JSON.stringify(value), JSON.stringify(interestLocation), initialProvince ]); // JSON.stringify for deep comparison

                // handleOnChange should be called only by User's interaction with LocationSelector
                // We don't want to get userLocation updated by initialCity or initialProvince
                function handleOnChange(newCity) {
                    inputProps.onChange(newCity);

                    if (newCity == null) {
                        return;
                    }

                    sessionStore.setInterestLocation(getLocationFromCity(newCity));
                }

                return (
                    <CityInput
                        {...inputProps}
                        value={currentValue}
                        onChange={handleOnChange} // custom onChange behavior
                        isDisabled={disabled}

                        label={label}
                        placeholder={placeholder}
                    />
                );
            }}
        </InputController>
    );
}

LocationSelector.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string.isRequired,

    label: MessagePropType,
    placeholder: MessagePropType,
    
    onChange: PropTypes.func,
    onValidityChange: PropTypes.func,

    value: LegacyCityPropType,
    initialProvince: PropTypes.oneOf(Object.values(PROVINCE)),

    isRequired: PropTypes.bool,
    isDisabled: PropTypes.bool,

    // note: if isVisible is defined, we add a container (targeted by className) to animate visibility
    isVisible: PropTypes.bool,
    animationType: AnimationTypePropType,

    className: PropTypes.string,
};

/**
 * @typedef {import('../definitions/LegacyCityPropType').LegacyCityPropType} LegacyCityPropType
 * @typedef {import('../definitions/CityPropType').CityPropType} CityPropType
 * @typedef {import('../definitions/CityPropType').ProvinceValues} ProvinceValues
 * 
 * @param {object} defaults
 * @param {LegacyCityPropType} defaults.value
 * @param {ProvinceValues} defaults.initialProvince
 * @param {CityPropType} defaults.interestLocation
 * 
 * @returns {LegacyCityPropType}
 */
function getDefaultCity({ value, initialProvince, interestLocation }) {
    // Always prefer city if provided
    if (typeof value?.name === 'string' && value?.province?.code === 'string') {
        return value;
    }

    // User location should be ignored if it doesn't match initialProvince
    if (typeof initialProvince === 'string' && interestLocation != null) {
        return interestLocation.province !== initialProvince
            ? getProvincePrincipalCity(initialProvince)
            : getCityFromLocation(interestLocation);
    }

    return getCityFromLocation(interestLocation) ?? getProvincePrincipalCity(initialProvince ?? PROVINCE.ONTARIO);
}

export default observer(LocationSelector);
